import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalHeader } from '@heroui/react';
import { AppButton } from '@/shared/ui/AppButton';
import { SectionLoader } from '@/shared/ui/Loader';

type ScormFrameStatus = 'idle' | 'loading' | 'ready' | 'error';

interface ScormPreviewModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  previewUrl: string | null;
}

export const ScormPreviewModal = memo(
  ({ isOpen, onOpenChange, previewUrl }: ScormPreviewModalProps) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const timeoutRef = useRef<number | null>(null);
    const [status, setStatus] = useState<ScormFrameStatus>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const iframeSrc = useMemo(() => previewUrl ?? '', [previewUrl]);

    const clearLoadTimeout = useCallback(() => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }, []);

    useEffect(() => {
      if (!isOpen || !iframeSrc) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setStatus('idle');
        setErrorMessage('');
        clearLoadTimeout();
        return;
      }

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStatus('loading');
      setErrorMessage('');

      let backendOrigin: string;
      try {
        backendOrigin = new URL(iframeSrc).origin;
      } catch {
        setStatus('error');
        setErrorMessage('The lesson could not be opened. Please try again.');
        return;
      }

      const handleMessage = (event: MessageEvent) => {
        if (event.source !== iframeRef.current?.contentWindow) return;
        if (event.origin !== backendOrigin) return;
        if (event.data?.source !== 'ihcl-scorm') return;

        switch (event.data.event) {
          case 'loading':
          case 'content-loaded':
            // Bootstrap и wrapper навигируются в одном iframe. Позднее load-событие
            // не должно вернуть уже готовый/error preview обратно в loading.
            setStatus((current) =>
              current === 'ready' || current === 'error' ? current : 'loading',
            );
            break;
          case 'ready':
            clearLoadTimeout();
            setStatus('ready');
            break;
          case 'error':
            clearLoadTimeout();
            setStatus('error');
            setErrorMessage(
              event.data.message || 'The lesson could not be opened. Please try again.',
            );
            break;
        }
      };

      clearLoadTimeout();
      timeoutRef.current = window.setTimeout(() => {
        setStatus('error');
        setErrorMessage(
          'The lesson is taking too long to load. Please close the preview and try again.',
        );
        timeoutRef.current = null;
      }, 35_000);

      window.addEventListener('message', handleMessage);
      return () => {
        clearLoadTimeout();
        window.removeEventListener('message', handleMessage);
      };
    }, [iframeSrc, isOpen, clearLoadTimeout]);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (!open) {
          setStatus('idle');
          setErrorMessage('');
          clearLoadTimeout();
        }
        onOpenChange(open);
      },
      [onOpenChange, clearLoadTimeout],
    );

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        placement="center"
        size="5xl"
        scrollBehavior="inside"
        classNames={{
          base: 'p-4 flex flex-col gap-4 max-w-[95vw] h-[90vh]',
          wrapper: 'overflow-hidden',
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex justify-between items-center">
                <span>SCORM Preview</span>
                <AppButton color="white" onPress={() => handleOpenChange(false)}>
                  Close
                </AppButton>
              </ModalHeader>
              <ModalBody className="relative flex-1 p-0 min-h-0">
                {status === 'loading' && (
                  <SectionLoader className="absolute inset-0 z-20 bg-white" />
                )}
                {status === 'error' && (
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white p-6 text-center">
                    <p className="text-lg font-semibold text-danger">Unable to open this lesson</p>
                    <p className="max-w-md text-sm text-grey-secondary">{errorMessage}</p>
                  </div>
                )}
                {iframeSrc && (
                  <iframe
                    key={iframeSrc}
                    ref={iframeRef}
                    title="SCORM preview"
                    src={iframeSrc}
                    aria-busy={status === 'loading'}
                    className="h-full min-h-[70vh] w-full rounded-lg border-0"
                  />
                )}
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

ScormPreviewModal.displayName = 'ScormPreviewModal';
